import { eventChannel, END, EventChannel } from 'redux-saga';
import {
  actionChannel,
  ActionChannelEffect,
  ActionPattern,
  call,
  fork,
  ForkEffect,
  put,
  take,
  TakeEffect,
} from 'redux-saga/effects';

import * as Types from '../actions';

function createVideoRoomPluginChannel(
  plugin: PluginHandleEmitter,
  isRemote: boolean,
  userId: number | undefined
): EventChannel<Types.VideoRoomPluginAction> {
  return eventChannel((emitter) => {
    function consentDialog(on: boolean) {
      emitter({
        type: plugin.events.CONSENT_DIALOG,
        payload: { userId, isRemote, on },
      });
    }
    function mediaState(media: 'audio' | 'video', receiving: boolean) {
      emitter({
        type: plugin.events.MEDIA_STATE,
        payload: { userId, isRemote, media, receiving },
      });
    }
    function webrtcState(on: boolean, reason: string) {
      emitter({
        type: plugin.events.WEBRTC_STATE,
        payload: { userId, isRemote, on, reason },
      });
    }
    function slowLink(uplink: boolean, lost: number) {
      emitter({
        type: plugin.events.SLOW_LINK,
        payload: { userId, isRemote, uplink, lost },
      });
    }
    function iceState(state: RTCIceConnectionState) {
      emitter({
        type: plugin.events.ICE_STATE,
        payload: { userId, isRemote, state },
      });
    }
    function onDataChannelOpen(...args: any[]) {
      emitter({
        type: plugin.events.DATA_OPEN,
        payload: { isRemote, userId, ...args },
      });
    }
    function onDataChannelMessage(message: string) {
      emitter({
        type: plugin.events.DATA,
        payload: { isRemote, userId, message },
      });
    }
    function detached() {
      emitter({
        type: plugin.events.DETACHED,
        payload: { userId, isRemote },
      });
    }
    function cleanup() {
      emitter(END);
    }
    plugin.addListener(plugin.events.CONSENT_DIALOG, consentDialog);
    plugin.addListener(plugin.events.MEDIA_STATE, mediaState);
    plugin.addListener(plugin.events.WEBRTC_STATE, webrtcState);
    plugin.addListener(plugin.events.SLOW_LINK, slowLink);
    plugin.addListener(plugin.events.ICE_STATE, iceState);
    plugin.addListener(plugin.events.DATA_OPEN, onDataChannelOpen);
    plugin.addListener(plugin.events.DATA, onDataChannelMessage);
    plugin.addListener(plugin.events.DETACHED, detached);
    plugin.addListener(plugin.events.CLEANUP, cleanup);
    return () => {
      Object.keys(plugin.events).forEach((key) =>
        plugin.removeAllListeners(
          plugin.events[key as keyof PluginHandleEvents]
        )
      );
    };
  });
}

function* watchVideoRoomPluginEvents(
  action: Types.ClientAttachPluginSuccessAction
) {
  const { isRemote, plugin, userId } = action.payload;
  const pluginActionChannel: EventChannel<Types.VideoRoomPluginAction> = yield call(
    createVideoRoomPluginChannel,
    plugin,
    isRemote,
    userId
  );
  while (true) {
    try {
      const action: Types.VideoRoomPluginAction = yield take(
        pluginActionChannel
      );
      yield put(action);
    } catch (e) {
      yield put({
        type: 'VIDEOROOM_PLUGIN_CHANNEL_ERROR',
        error: e.message || e,
      });
    }
  }
}

function* handlewNewVideoRoomPlugin(): Generator<
  ActionChannelEffect | TakeEffect | ForkEffect<void>,
  void
> {
  const requestChannel = (yield actionChannel(
    Types.CLIENT_ATTACH_PLUGIN_SUCCESS
  )) as ActionPattern<Types.ClientAttachPluginSuccessAction>;
  while (true) {
    const action = (yield take(
      requestChannel
    )) as Types.ClientAttachPluginSuccessAction;
    yield fork(watchVideoRoomPluginEvents, action);
  }
}

export default [handlewNewVideoRoomPlugin()];
